import React, { useState, useEffect, useMemo, useCallback } from "react";
import DatePicker from "react-datepicker";
import { hr } from "date-fns/locale";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import "./UserReport.css";
import { ReportAPI } from "../../Api/ReportAPI";

function convertToLocalDate(totalDate) {
  const parts = totalDate.split("-");
  if (parts.length !== 3) return new Date();

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);

  const dateObj = new Date(year, month, day);
  dateObj.setHours(0, 0, 0, 0);
  return dateObj;
}

const TotalCalendar = ({ selectedDate, setSelectedDate, availableDates }) => (
  <div className="calendar-container">
    <label htmlFor="total-date">Odaberite datum: </label>
    <DatePicker
      id="total-date"
      selected={selectedDate}
      onChange={(date) => setSelectedDate(date)}
      includeDates={availableDates}
      locale={hr}
      dateFormat="dd MMMM yyyy"
      placeholderText="Odaberite datum"
      className="custom-datepicker"
    />
  </div>
);

const FondInventoryTotal = () => {
  const [totalFiles, setTotalFiles] = useState([]);
  const [loadingTotal, setLoadingTotal] = useState(true);
  const [totalError, setTotalError] = useState(null);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [selectedTotalFile, setSelectedTotalFile] = useState(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  useEffect(() => {
    const fetchTotalFiles = async () => {
      try {
        const response = await ReportAPI.getFondTotalReports();
        const fetchedTotalFiles = response.data || [];

        setTotalFiles(fetchedTotalFiles);

        if (fetchedTotalFiles.length > 0) {
          setSelectedTotalFile(fetchedTotalFiles[0]);
          setSelectedCalendarDate(convertToLocalDate(fetchedTotalFiles[0].date));
        }
      } catch (error) {
        console.error(error);
        setTotalError("Došlo je do pogreške prilikom dohvaćanja total datoteka.");
      } finally {
        setLoadingTotal(false);
      }
    };

    fetchTotalFiles();
  }, []);

  useEffect(() => {
    let objectUrl = null;

    if (!selectedTotalFile) return;

    ReportAPI.getReportFile(selectedTotalFile.id, 0)
      .then((response) => {
        const file = new Blob([response.data], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(file);
        setCurrentPdfUrl(objectUrl);
      })
      .catch((error) => {
        console.error("Greška pri učitavanju total PDF-a:", error);
      });

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedTotalFile]);

  const availableDates = useMemo(() => {
    const datesSet = new Set();
    totalFiles.forEach((file) => {
      datesSet.add(convertToLocalDate(file.date).getTime());
    });

    return Array.from(datesSet)
      .map((time) => new Date(time))
      .sort((a, b) => b.getTime() - a.getTime());
  }, [totalFiles]);

  const filteredTotalFiles = useMemo(() => {
    return totalFiles.filter((file) => {
      const fileDate = convertToLocalDate(file.date);
      return fileDate.getTime() === selectedCalendarDate?.getTime();
    });
  }, [totalFiles, selectedCalendarDate]);

  useEffect(() => {
    if (filteredTotalFiles.length > 0) {
      setSelectedTotalFile(filteredTotalFiles[0]);
      setIframeKey((prevKey) => prevKey + 1);
    } else {
      setSelectedTotalFile(null);
      setCurrentPdfUrl(null);
    }
  }, [selectedCalendarDate, filteredTotalFiles]);

  const handleTotalFileClick = useCallback((file) => {
    setSelectedTotalFile(file);
    setIframeKey((prevKey) => prevKey + 1);
  }, []);

  const isMobile = window.innerWidth < 768;

  return (
    <div className="container">
      <div className="header">
        <h2>Fond Inventura - Ukupni pregled</h2>
        <TotalCalendar
          selectedDate={selectedCalendarDate}
          setSelectedDate={setSelectedCalendarDate}
          availableDates={availableDates}
        />
        {filteredTotalFiles.length > 0 && selectedTotalFile && (
          <span>
            {format(convertToLocalDate(selectedTotalFile.date), "dd MMMM yyyy")} - Ukupni pregled
          </span>
        )}
      </div>

      {loadingTotal ? (
        <div className="spinner-container">
          <div className="spinner"></div>
          <p>Učitavanje total datoteka...</p>
        </div>
      ) : totalError ? (
        <div className="error-message">
          <p>{totalError}</p>
        </div>
      ) : filteredTotalFiles.length === 0 ? (
        <div className="no-report-message">
          Nema total datoteka za odabrani dan
        </div>
      ) : (
        <>
          <div className="date-list">
            {filteredTotalFiles.map((file) => (
              <div
                key={file.id}
                className={`date-item ${
                  selectedTotalFile && selectedTotalFile.id === file.id ? "selected" : ""
                }`}
                onClick={() => handleTotalFileClick(file)}
                role="button"
                tabIndex="0"
              >
                📋 Ukupni pregled - {file.date}
              </div>
            ))}
          </div>

          <div className="iframe-container">
            {isMobile ? (
              <div className="mobile-report">
                <p>PDF pregled nije podržan na mobilnom uređaju.</p>
                {currentPdfUrl ? (
                  <a href={currentPdfUrl} target="_blank" rel="noopener noreferrer">
                    Kliknite ovdje za otvaranje total datoteke
                  </a>
                ) : null}
              </div>
            ) : (
              selectedTotalFile &&
              currentPdfUrl && (
                <iframe
                  key={iframeKey}
                  title={`Total fond - ${selectedTotalFile.date}`}
                  src={currentPdfUrl}
                  width="100%"
                  height="1200px"
                />
              )
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FondInventoryTotal;